import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { connect } from 'react-redux';

import { loadResourcesAction } from "actions/ClinicActions";
import { RIGHT } from "constants/TooltipPositionIds";
import { CLINIC_TAGS } from "constants/AdminControls";
import * as LocationTypes from "constants/LocationTypes";

import { userPrimaryLocationType } from "utils/permissions/rolesPermissions";
import { getClinicInfo } from "utils/ClinicData";
import getProviderId from "utils/getProviderId";
import { getNodeId } from "utils/AdminData";

import AdminBase from "components/layout/AdminBase";
import ClinicBase from "components/layout/ClinicBase";
import ProviderBase from "components/layout/ProviderBase";
import Accordion from "components/common/Accordion/Accordion";
import AccordionSection from "components/common/Accordion/AccordionSection";
import Tooltip from "components/common/Tooltip";
import Tabs from "components/common/Tabs/Tabs";
import Tile from "components/common/Tile/Tile";
import SpinnerTakeover from "components/common/SpinnerTakeover";

import styles from './Resources.scss';

const Resources = ({
    resources = [],
    isLoading = false,
    loadResources,
    userProfile = {},
    clinicId,
    providerId,
    nodeId,
    clinicHasPrioritySupport = false,
    location = {}
}) => {
    const [activeProviderId, setActiveProviderId] = useState(null);

    const isProviderView = (
        (userPrimaryLocationType(userProfile, [LocationTypes.PROVIDER]) && !clinicId) ||
        (userPrimaryLocationType(userProfile, [LocationTypes.NULL]) && providerId && nodeId)
    );

    const getProviderLabel = (providerId) => {
        const id = String(providerId);
        const providerLabels = {
            "1": "BI",
            "6": "Elanco",
            "8": "Merck",
            "13": "Zoetis"
        };
        return providerLabels[id] || "Unknown Provider";
    };

    const getProviderImage = (providerId) => {
        const providerImages = {
            "1": "https://gldevcdn.azureedge.net/images/providers/BoehringerIngelheimLogo.svg",
            "6": "https://gldevcdn.azureedge.net/images/providers/ElancoLogo.svg",
            "8": "https://gldevcdn.azureedge.net/images/providers/MerckLogo.svg",
            "13": "https://gldevcdn.azureedge.net/images/providers/ZoetisLogo.svg"
        };
        return providerImages[providerId];
    };

    const getEmptyStateText = (type, providerId) => {
        const providerName = providerId ? getProviderLabel(providerId) : 'General';
        return `There are no ${providerName} ${type}`;
    };

    const handleGuideClick = (guide) => {
        if (!guide.PdfUrl) return;

        const pdfUrls = Array.isArray(guide.PdfUrl) ? guide.PdfUrl : [guide.PdfUrl];

        pdfUrls.forEach((url, index) => {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            const filename = pdfUrls.length > 1
                ? `${guide.Title}_${index + 1}.pdf`
                : guide.Title;
            link.download = filename || 'guide.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };

    useEffect(() => {
        loadResources();
    }, [loadResources]);

    const safeResources = Array.isArray(resources) ? resources : [];

    const providerTabs = [
        { label: "General", providerId: null },
        ...(safeResources
            .filter(resource => resource?.ProviderID != null)
            .map(resource => ({
                label: getProviderLabel(String(resource.ProviderID)),
                providerId: Number(resource.ProviderID),
                image: getProviderImage(String(resource.ProviderID))
            })) || [])
    ];

    const activeResource = resources?.find(resource =>
        Number(resource.ProviderID) === activeProviderId
    ) || resources?.find(resource => resource.ProviderID === null);

    const handleTabChange = (tab) => {
        setActiveProviderId(tab.providerId);
    };

    const content = isLoading ? (
        <SpinnerTakeover show />
    ) : (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                padding: '20px',
                flexDirection: 'column',
            }}>
            <h1 className={styles.header}>Resources</h1>
            <p className={styles.subHeader}>Filter resources by provider</p>
            <Tabs
                tabs={providerTabs}
                onTabChange={handleTabChange}
                activeId={activeProviderId}
            />

            <div className={styles.resourceContainer}>
                <div>
                    <div className={styles.subheaderContainer}>
                        <h2 className={styles.subheader}>Downloadable Guides</h2>
                        <Tooltip
                            position={RIGHT}
                            tip="Click on a guide to download it, then print it out for your team!"
                        >
                            <div className="flex-none">
                                <i id={styles.infoIcon} className={"fa fa-info-circle"} />
                            </div>
                        </Tooltip>
                    </div>
                    <div className={styles.tileContainer}>
                        {!activeResource?.Guides?.length ? (
                            <div className={styles.emptyState}>
                                {getEmptyStateText('guides', activeProviderId)}
                            </div>
                        ) : (
                            activeResource.Guides.map((guide, index) => (
                                <Tile
                                    key={guide.GuideID || index}
                                    imageSrc={guide.ImageUrl || undefined}
                                    title={guide.Title}
                                    onClick={() => handleGuideClick(guide)}
                                />
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className={styles.subheaderContainer}>
                        <h2 className={styles.subheader}>Frequently Asked Questions</h2>
                    </div>
                    {!activeResource?.FAQs?.length ? (
                        <div className={styles.emptyState}>
                            {getEmptyStateText('FAQs', activeProviderId)}
                        </div>
                    ) : (
                        <Accordion
                        theme={{
                            root: styles.accordionRoot,
                            header: styles.headerSmall,
                            collapse: styles.collapse,
                            content: styles.collapseContent,
                        }}
                        multiOpen={true}
                        defaultOpen={[]}
                    >
                        {activeResource.FAQs.map((faq) => (
                            <AccordionSection
                                key={faq.FAQID}
                                id={faq.FAQID}
                                header={faq.Question}
                                className={styles.accordionContainer}
                            >
                                {faq.Answer}
                            </AccordionSection>
                        ))}
                    </Accordion>
                    )}
                </div>
            </div>
        </div>
    );

    if (userPrimaryLocationType(userProfile, [LocationTypes.NULL]) && !clinicId && !providerId) {
        return (
            <AdminBase pageTitle="Resources">
                {content}
            </AdminBase>
        );
    } else if (isProviderView) {
        return (
            <ProviderBase
                location={location}
                providerId={providerId}
                nodeId={nodeId}
                clinicId={clinicId}
                pageTitle="Resources"
            >
                {content}
            </ProviderBase>
        );
    } else {
        return (
            <ClinicBase
                clinicId={clinicId}
                pageTitle={clinicHasPrioritySupport ? "Premier Information Page" : "Resources"}
                adminControls={CLINIC_TAGS}
            >
                {content}
            </ClinicBase>
        );
    }
};

Resources.propTypes = {
    resources: PropTypes.arrayOf(PropTypes.shape({
        ProviderID: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
            PropTypes.oneOf([null])
        ]),
        FAQs: PropTypes.arrayOf(PropTypes.shape({
            FAQID: PropTypes.number,
            Question: PropTypes.string,
            Answer: PropTypes.string
        })),
        Guides: PropTypes.arrayOf(PropTypes.shape({
            GuideID: PropTypes.number,
            Title: PropTypes.string,
            ImageUrl: PropTypes.string,
            PdfUrl: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.arrayOf(PropTypes.string)
            ])
        }))
    })),
    isLoading: PropTypes.bool,
    loadResources: PropTypes.func.isRequired,
    userProfile: PropTypes.object,
    clinicId: PropTypes.number,
    providerId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.oneOf([null])
    ]),
    nodeId: PropTypes.number,
    clinicHasPrioritySupport: PropTypes.bool,
    location: PropTypes.object
};

export default connect(
    (state, ownProps) => {
        const params = ownProps?.match?.params || {};
        const userProfile = state.user.userProfile;
        const adminClinicId = Number(ownProps.match.params.clinicId);
        const clinic = getClinicInfo(state, adminClinicId);
        const clinicId = clinic ? clinic.clinicId : undefined;
        const providerId = String(getProviderId(params, userProfile));
        const nodeId = Number(getNodeId(state, ownProps));
        const clinicHasPrioritySupport = !!clinic?.prioritySupport?.length;

        const resourcesData = state.entities?.resources?.data?.Resources;
        const normalizedResources = Array.isArray(resourcesData) ? resourcesData : [];

        return {
            resources: normalizedResources,
            isLoading: state.entities?.resources?.isLoading || false,
            error: state.entities?.resources?.error || null,
            userProfile,
            clinicId,
            providerId,
            nodeId,
            clinicHasPrioritySupport,
            location: ownProps.location
        };
    },
    { loadResources: loadResourcesAction }
)(Resources);